import { makePageList } from 'commons/utils/commonUtil';
import React, { useCallback, useMemo } from 'react';
import { PaginationProps } from 'components/Pagination/type';
import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

import { BackIcon, DoubleBackIcon, NextIcon, DoubleNextIcon } from 'asset/images';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '50px',
    display: 'flex',
    justifyContent: 'center',
    listStyle: 'none',
    padding: 0,
    '& > li': {
      width: '50px',
      height: '50px',
      paddingTop: '15px',
      fontSize: '12px',
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#999',
      cursor: 'pointer',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#b6b6b6',
    },
  },
  borderness: {
    borderWidth: '0  !important',
  },
  currentPage: {
    borderWidth: '1px',
    color: '#000 !important',
    backgroundColor: '#f3f3f3 !important',
    borderColor: '#000 !important',
  },
  disabled: {
    '&:hover': {
      cursor: 'not-allowed',
    },
  },
}));

function Pagination({ pagination, onChangePage }: PaginationProps) {
  const classes = useStyles();

  const pageList = useMemo(() => {
    const { pageCount, pageNumber } = pagination;
    return makePageList(pageNumber, pageCount);
  }, [pagination]);

  const isPrev = useMemo(() => {
    return pagination.pageNumber > 0;
  }, [pagination.pageNumber]);

  const isNext = useMemo(() => {
    const { pageNumber, pageCount } = pagination;
    return pageNumber < pageCount - 1;
  }, [pagination.pageNumber, pagination.pageCount]);

  const onClickPagination = useCallback(
    (event: React.MouseEvent) => {
      const { id } = event.currentTarget;

      onChangePage(parseInt(id));
    },
    [onChangePage],
  );

  const onClickStartPage = useCallback(() => {
    onChangePage(0);
  }, [onChangePage]);

  const onClickPrevPage = useCallback(() => {
    if (isPrev) {
      onChangePage(pagination.pageNumber - 1);
    }
  }, [onChangePage, pagination.pageNumber, isPrev]);

  const onClickEndPage = useCallback(() => {
    if (isNext) {
      const { pageCount } = pagination;
      onChangePage(pageCount - 1);
    }
  }, [onChangePage, pagination.pageCount, isNext]);

  const onClickNextPage = useCallback(() => {
    if (isNext) {
      onChangePage(pagination.pageNumber + 1);
    }
  }, [onChangePage, pagination.pageNumber, isNext]);

  return (
    <ul className={classes.root}>
      <li
        className={clsx(classes.borderness, !isPrev && classes.disabled)}
        onClick={onClickStartPage}
      >
        <img src={DoubleBackIcon} />
      </li>
      <li
        className={clsx(classes.borderness, !isPrev && classes.disabled)}
        onClick={onClickPrevPage}
      >
        <img src={BackIcon} />
      </li>
      {pageList.map(page => {
        return (
          <li
            key={page}
            id={String(page)}
            className={clsx(page === pagination.pageNumber && classes.currentPage)}
            onClick={onClickPagination}
          >
            {page + 1}
          </li>
        );
      })}
      {pageList.length === 0 && (
        <li key={1} id="1" className={classes.currentPage}>
          1
        </li>
      )}
      <li
        className={clsx(classes.borderness, !isNext && classes.disabled)}
        onClick={onClickNextPage}
      >
        <img src={NextIcon} />
      </li>
      <li
        className={clsx(classes.borderness, !isNext && classes.disabled)}
        onClick={onClickEndPage}
      >
        <img src={DoubleNextIcon} />
      </li>
    </ul>
  );
}

export default Pagination;
